import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography, Grow } from "@material-ui/core";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import CheckCircleOutlinedIcon from "@material-ui/icons/CheckCircleOutlined";
import NotificationsActiveOutlinedIcon from "@material-ui/icons/NotificationsActiveOutlined";
import EventIcon from "@material-ui/icons/Event";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import TuneOutlinedIcon from "@material-ui/icons/TuneOutlined";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { getUsers } from "../../actions/auth";
import * as api from "../../api";
import { ListPageSkeleton } from "../Skeletons";
import styles from "./SuperAdminLayout.module.css";
import { useSubscription } from "../../Context/SubscriptionContext";

// Landing panel for the sidebar - stat row + "Manage" tile grid, mirroring
// the reference dashboard layout the user asked to match. Pure client-side
// aggregation of data the Users/Subscription/Global Settings panels already
// fetch on their own - no new backend endpoint.
const OverviewTab = ({ onNavigate }) => {
  const dispatch = useDispatch();
  const { users, isLoading } = useSelector((state) => state.auth);
  // Shared with SuperAdminNavbar.js's chip and SubscriptionTab.js's Extend
  // panel (was: its own independent fetch, going stale after a save on the
  // Subscription tab) - see SubscriptionContext.js.
  const { subscription, loading: subLoading } = useSubscription();
  const [globalSettings, setGlobalSettings] = useState(null);
  const [loadingExtra, setLoadingExtra] = useState(true);

  useEffect(() => {
    dispatch(getUsers());
    // Guards against "state update on unmounted component" - this panel is
    // wrapped in Subscription.js's <Grow unmountOnExit>, so switching sidebar
    // tabs quickly (or React StrictMode's dev-only double-invoke right after
    // login) can unmount it before this fetch resolves. Same cancelled-flag
    // pattern UsersTab.js's viewStoreInfo fetch already uses.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.getGlobalSettings();
        if (!cancelled) setGlobalSettings(data?.data || {});
      } catch (error) {
        // Non-fatal - stat cards just render without those figures.
      } finally {
        if (!cancelled) setLoadingExtra(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (isLoading || loadingExtra || subLoading) {
    return <ListPageSkeleton columns={4} showToolbar={false} />;
  }

  const subscriptionDays = subscription?.subscriptionExpiry
    ? Math.ceil((new Date(subscription.subscriptionExpiry).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  // SuperAdmin accounts are platform operators, not store users - counting
  // them made "Total Users" read one higher than the number of people this
  // deployment actually serves, and they can't be deactivated or expired
  // anyway, so they'd skew every figure on this row.
  const isSuperAdminUser = (u) =>
    Array.isArray(u.userType) ? u.userType.includes("superAdmin") : u.userType === "superAdmin";
  const allUsers = (users || []).filter((u) => !isSuperAdminUser(u));
  const totalUsers = allUsers.length;
  const activeUsers = allUsers.filter((u) => u.isActive !== false).length;
  const reminderDays = globalSettings?.expiryReminderDays ?? 7;
  const usersExpiringSoon = allUsers.filter((u) => {
    if (!u.accountExpiry) return false;
    const daysLeft = Math.ceil((new Date(u.accountExpiry).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return daysLeft >= 0 && daysLeft <= reminderDays;
  }).length;
  // The deployment licence is the other thing on this dashboard that expires,
  // and it's the one a SuperAdmin actually controls - counting only per-user
  // accountExpiry meant a deployment sitting at 0 days still read
  // "0 Expiring Soon", which is exactly what looked broken.
  const subscriptionExpiringSoon = subscriptionDays !== null && subscriptionDays <= reminderDays;
  const expiringSoon = usersExpiringSoon + (subscriptionExpiringSoon ? 1 : 0);
  const showExpiryBadge = globalSettings?.showExpiryBadge !== false;

  const subscriptionTone =
    subscriptionDays === null
      ? "neutral"
      : subscriptionDays < 0
      ? "danger"
      : subscriptionDays <= reminderDays
      ? "warning"
      : "success";

  const expiringSoonHint = () => {
    if (!subscriptionExpiringSoon) return `Within ${reminderDays} days`;
    if (usersExpiringSoon) return `${usersExpiringSoon} account(s) + this deployment`;
    return "This deployment's licence";
  };

  const subscriptionHint = () => {
    if (subscriptionDays === null) return "No expiry set";
    if (subscriptionDays < 0) return "Expired - users are locked out";
    if (subscriptionDays === 0) return "Expires today";
    return "Remaining";
  };

  const stats = [
    {
      label: "Total Users",
      value: totalUsers,
      icon: PeopleAltOutlinedIcon,
      tone: "neutral",
      hint: "Store users, excludes SuperAdmin",
    },
    {
      label: "Active Users",
      value: activeUsers,
      icon: CheckCircleOutlinedIcon,
      tone: "success",
      hint: `${totalUsers - activeUsers} deactivated`,
    },
    ...(showExpiryBadge
      ? [
          {
            label: "Expiring Soon",
            value: expiringSoon,
            icon: NotificationsActiveOutlinedIcon,
            tone: expiringSoon > 0 ? "warning" : "neutral",
            hint: expiringSoonHint(),
          },
        ]
      : []),
    {
      label: "Subscription",
      value:
        subscriptionDays === null
          ? "-"
          : subscriptionDays < 0
          ? `${Math.abs(subscriptionDays)}d ago`
          : `${subscriptionDays}d`,
      icon: EventIcon,
      tone: subscriptionTone,
      hint: subscriptionHint(),
    },
  ];

  const tiles = [
    { key: "users", title: "Users", desc: "Activate, deactivate, delete, and set expiry for any user.", icon: PeopleAltIcon },
    { key: "subscription", title: "Subscription", desc: "Extend or set the deployment-wide license expiry.", icon: EventIcon },
    { key: "settings", title: "Global Settings", desc: "Invoice format, Hold KOT, shift tracking, and more - for every store.", icon: TuneOutlinedIcon },
  ];

  return (
    <div>
      <div className={styles.statRow}>
        {stats.map((s, i) => (
          <Grow in timeout={220 + i * 80} key={s.label}>
            <div className={`${styles.statCard} ${styles[`statCard_${s.tone}`] || ""}`}>
              <span className={`${styles.statIcon} ${styles[`statIcon_${s.tone}`] || ""}`}>
                <s.icon fontSize="small" />
              </span>
              <div style={{ minWidth: 0 }}>
                <div className={`${styles.statValue} ${styles[`statValue_${s.tone}`] || ""}`}>{s.value}</div>
                <div className={styles.statLabel}>{s.label}</div>
                {s.hint && <div className={styles.statHint}>{s.hint}</div>}
              </div>
            </div>
          </Grow>
        ))}
      </div>

      <Typography variant="h6" className={styles.sectionTitle}>
        Manage
      </Typography>
      <div className={styles.manageGrid}>
        {tiles.map((t, i) => (
          <Grow in timeout={260 + i * 80} key={t.key}>
            <div
              className={styles.manageTile}
              onClick={() => onNavigate(t.key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" ? onNavigate(t.key) : null)}
            >
              <span className={styles.statIcon}>
                <t.icon fontSize="small" />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={styles.manageTileTitle}>{t.title}</div>
                <div className={styles.manageTileDesc}>{t.desc}</div>
              </div>
              {/* These tiles have always been clickable but nothing said so. */}
              <ChevronRightIcon className={styles.manageTileArrow} fontSize="small" />
            </div>
          </Grow>
        ))}
      </div>
    </div>
  );
};

export default OverviewTab;
